import { useMemo, useState } from 'react';

import { useUrlFilters } from '@/hooks/useUrlFilters';
import type { McpWithUserDataResponse, ServiceDomainResponse } from '@/openapi/schema';
import { SERVICE_DOMAIN_KEY } from '@/pages/explorer/filters/ServiceDomainFilter';
import { STAGE_FILTER_KEY } from '@/pages/explorer/filters/StageFilter';
import { TAG_FILTER_KEY } from '@/pages/explorer/filters/TagFilter';
import { CLASSIFICATION_FILTER_KEY } from '@/pages/explorer/filters/TypeFilter';

export function getMcpClassification(mcp: McpWithUserDataResponse): string {
  return mcp.classification.toLowerCase();
}

export function getMcpStage(mcp: McpWithUserDataResponse): string {
  return mcp.stage.toLowerCase();
}

function getServiceDomainOptions(
  mcps: McpWithUserDataResponse[] | undefined,
  serviceDomains: ServiceDomainResponse[] | undefined,
) {
  if (!mcps || !serviceDomains) return [];

  const serviceDomainIdsFromMcps = new Set(
    mcps.map((mcp) => mcp.serviceDomain?.id).filter((id): id is string => id != null),
  );

  return serviceDomains
    .map((serviceDomain) => ({
      content: [serviceDomain.serviceDomainId, serviceDomain.name],
      selectedKey: serviceDomain.id,
      selectedValue: serviceDomain.name,
    }))
    .filter((serviceDomain) => serviceDomainIdsFromMcps.has(serviceDomain.selectedKey));
}

function getStageOptions(mcps: McpWithUserDataResponse[] | undefined) {
  if (!mcps) return [];

  return Array.from(new Set(mcps.map((mcp) => getMcpStage(mcp)))).map((stage) => ({
    selectedKey: stage,
    content: [stage],
  }));
}

function getTypeOptions(mcps: McpWithUserDataResponse[] | undefined) {
  if (!mcps) return [];

  return ['all', ...Array.from(new Set(mcps.map((mcp) => getMcpClassification(mcp))))];
}

function getTagOptions(mcps: McpWithUserDataResponse[] | undefined) {
  if (!mcps) return [];

  return Array.from(new Set(mcps.flatMap((mcp) => mcp.tags))).map((tag) => ({
    selectedKey: tag,
    content: [tag],
  }));
}

function filterMcps(
  mcps: McpWithUserDataResponse[] | undefined,
  query: string,
  stageFilter: string[],
  typeFilter: string[],
  serviceDomainFilter: string[],
  tagFilter: string[],
) {
  if (!mcps) return [];

  const lowerCasedQuery = query.toLowerCase();

  return mcps
    .filter((mcp) => stageFilter.length === 0 || stageFilter.includes(getMcpStage(mcp)))
    .filter(
      (mcp) => typeFilter.length === 0 || typeFilter[0] === 'all' || typeFilter.includes(getMcpClassification(mcp)),
    )
    .filter((mcp) => serviceDomainFilter.length === 0 || serviceDomainFilter.includes(mcp.serviceDomain?.id ?? ''))
    .filter((mcp) => tagFilter.length === 0 || mcp.tags.some((tag) => tagFilter.includes(tag)))
    .filter(
      (mcp) =>
        !lowerCasedQuery ||
        mcp.name.toLowerCase().includes(lowerCasedQuery) ||
        mcp.slug.toLowerCase().includes(lowerCasedQuery) ||
        mcp.description?.toLowerCase().includes(lowerCasedQuery),
    );
}

export function useMcpFilters(
  mcps: McpWithUserDataResponse[] | undefined,
  serviceDomains: ServiceDomainResponse[] | undefined,
) {
  const { getSelectedKeysFromURL } = useUrlFilters();
  const [query, setQuery] = useState('');

  const serviceDomainOptions = useMemo(() => getServiceDomainOptions(mcps, serviceDomains), [mcps, serviceDomains]);

  const serviceDomainFilter = useMemo(
    () => getSelectedKeysFromURL(SERVICE_DOMAIN_KEY, serviceDomainOptions),
    [getSelectedKeysFromURL, serviceDomainOptions],
  );

  const stageOptions = useMemo(() => getStageOptions(mcps), [mcps]);

  const stageFilter = useMemo(
    () => getSelectedKeysFromURL(STAGE_FILTER_KEY, stageOptions),
    [getSelectedKeysFromURL, stageOptions],
  );

  const typeOptions = useMemo(() => getTypeOptions(mcps), [mcps]);

  const typeFilter = useMemo(
    () =>
      getSelectedKeysFromURL(
        CLASSIFICATION_FILTER_KEY,
        typeOptions.map((type) => ({
          selectedKey: type,
          content: [type],
        })),
      ),
    [getSelectedKeysFromURL, typeOptions],
  );

  const tagOptions = useMemo(() => getTagOptions(mcps), [mcps]);

  const tagFilter = useMemo(
    () => getSelectedKeysFromURL(TAG_FILTER_KEY, tagOptions),
    [getSelectedKeysFromURL, tagOptions],
  );

  const filteredMcps = useMemo(() => {
    return filterMcps(mcps, query, stageFilter, typeFilter, serviceDomainFilter, tagFilter);
  }, [mcps, query, serviceDomainFilter, stageFilter, tagFilter, typeFilter]);

  return {
    filteredMcps,
    query,
    serviceDomainFilter,
    serviceDomainOptions,
    setQuery,
    stageFilter,
    stageOptions,
    tagFilter,
    tagOptions,
    typeFilter,
    typeOptions,
  };
}
